import { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import HTMLRenderer from 'components/HTMLRenderer/HTMLRenderer';
import { HeaderLogo } from 'components/HeaderMain/HeaderMain';
import ModalV2 from 'components/Modal/ModalV2/ModalV2';
import ModalLayout from 'layouts/ModalLayout/ModalLayout';
import { fetchFinancePlanDisclosure } from 'store/redux/accounts/actions';
import { useAppDispatch } from 'store/redux/use-app-dispatch';
import styles from './disclosure-modal.module.css';

const cx = classNames.bind(styles);

type DisclosureModalProps = {
  setShow?: (show: boolean) => void;
  show?: boolean;
  installments: number;
  balanceIds: string[];
};

export function DisclosureModal({
  setShow,
  show,
  installments,
  balanceIds,
}: DisclosureModalProps) {
  const [loading, setLoading] = useState(false);
  // Typed rather than reseeded: `useState(null)` gave the state the type `null`,
  // so it could never hold the disclosure document the effect fetches.
  const [html, setHtml] = useState<string | null>(null);
  // `useAppDispatch` rather than `useDispatch`: `clientMiddleware` resolves a
  // `promise` action to the client's result, which is what the effect awaits.
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchDisclosure = async () => {
      setLoading(true);
      try {
        const document = await dispatch(
          fetchFinancePlanDisclosure({
            installments,
            balanceUniqueIds: balanceIds,
          }),
        );

        setHtml(document);
        setLoading(false);
      } catch (err) {
        console.error(err);
        if (setShow) setShow(false);
        setLoading(false);
      }
    };

    fetchDisclosure();
  }, [dispatch, balanceIds, installments, setShow]);

  function handleClose() {
    if (setShow) setShow(false);
  }

  return (
    <ModalV2
      noBorder
      noPadding
      showSpinner={loading}
      onHide={handleClose}
      show={show}
    >
      <div className="absolute -top-xl md:top-sm">
        <HeaderLogo />
      </div>
      <ModalLayout>
        {html && <HTMLRenderer className={cx('disclosure')} html={html} />}
      </ModalLayout>
    </ModalV2>
  );
}
